import { useEffect, useState } from 'react'
import { registerScreen, initSSE, sendHealthPing, ensureSSE, resolveUrl } from '../../api'
import { Screen, SignageHwInfo, SignageDeviceInfo, SCREEN_HEALTH_PING_INTERVAL_MS } from 'common/types'
import { isNewScreen } from '../../utils'
import { UAParser } from 'ua-parser-js'
import { ScreenContainer } from './ScreenContainer'
import dayjs from 'dayjs'
import { Pairing } from './Pairing'
import { SourcePlayer } from './SourcePlayer'
import { getLogger } from '../../logger'
import { isNativeApp, getNativeConfig, syncCacheConf, setCacheTargets, setScreenInfo } from '../../native-bridge'
import { getCachablePlayablesUrls, toCacheableResource } from 'common/utils'
import { initServiceWorker } from '../../serviceWorkerManager'
import { getAutoplayCheckPromise } from '../../autoplay'

declare var __APP_VERSION: string

const log = getLogger('screen-manager')

const localStorageKey = 'screen'

// Platforms where the operator is holding or sitting at the device and can
// paste the code elsewhere. TV platforms are driven by a remote, where a
// clipboard button is useless.
const COPYABLE_PAIRCODE_PLATFORMS = new Set<Screen['platform']>(['web', 'macos', 'windows', 'linux'])

// Sync local playback mode to native layer and service worker
async function syncLocalPlayback(enabled: boolean, platform: Screen['platform']): Promise<void> {
  // Native layer (desktop Rust proxy + Android OkHttp)
  syncCacheConf(enabled ? 'serve' : 'warm')
  // Enable/disable service worker for web platform (other platforms handle caching natively)
  if (platform === 'web') {
    await initServiceWorker(enabled)
  }
}

async function getSignageDeviceInfo(canPlayUnmutedMedia: boolean): Promise<SignageDeviceInfo> {
  const browserInfo = UAParser(window.navigator.userAgent)
  const timezone = dayjs.tz.guess()
  let hwInfo: SignageHwInfo = {
    os: 'web',
    appVersion: __APP_VERSION,
  }

  if (isNativeApp()) {
    const nativeConfig = await getNativeConfig()
    console.log('Native config:', nativeConfig)
    if (nativeConfig?.hwInfo) {
      hwInfo = nativeConfig.hwInfo
    } else {
      // Fallback if config not available
      hwInfo = { os: 'android' }
    }
  }

  return {
    browserInfo,
    hwInfo,
    timezone,
    canPlayUnmutedMedia,
    screenResolution: { width: screen.width, height: screen.height },
    touchSupported: navigator.maxTouchPoints > 0,
  }
}

export interface ScreenInit {
  id?: string
  platform?: string
  device?: SignageDeviceInfo
}

function getPersistedScreen(): Screen | null {
  try {
    const screenStr = localStorage.getItem(localStorageKey)
    if (screenStr) {
      return JSON.parse(screenStr)
    }
  } catch (e) {
    console.log('Failed to get screen info from local storage', e)
  }
  return null
}

function setPersistedScreen(screen: Screen): void {
  try {
    localStorage.setItem(localStorageKey, JSON.stringify(screen))
  } catch (e) {
    console.log('Failed to set screen info to local storage', e)
  }
}

async function getScreenInfo(canPlayUnmutedMedia: boolean): Promise<ScreenInit> {
  const persistedScreen = getPersistedScreen()
  if (persistedScreen) return persistedScreen

  const deviceInfo = await getSignageDeviceInfo(canPlayUnmutedMedia)
  const newScreen = {
    platform: deviceInfo.hwInfo.os,
    device: deviceInfo,
  }

  return newScreen
}

function isNetworkError(error: any): boolean {
  // 502/503 from the local proxy means the upstream is unreachable (desktop offline)
  if (error?.statusCode === 502 || error?.statusCode === 503) return true
  // Network errors typically don't have a statusCode (fetch failed before getting a response)
  return !error?.statusCode && (error instanceof TypeError || error?.message?.includes('fetch'))
}

async function register(_screen: ScreenInit): Promise<Screen> {
  try {
    const screen = await registerScreen(_screen).catch((e) => {
      if (!isNewScreen(_screen) && e.statusCode === 404) {
        // conveniently create a new screen in case the old one was deleted
        const { platform, device } = _screen
        return registerScreen({
          platform,
          device,
        })
      }
      throw e
    })

    // Store screen data with minimal playlist/channel info (just IDs for offline mode)
    const { channel, playlist, schedule, ...rest } = screen
    const persistedScreen = {
      ...rest,
      // Store minimal references for offline playback
      playlist: playlist ? { id: playlist.id } : null,
      channel: channel ? { id: channel.id } : null,
      schedule: schedule ? { id: schedule.id, name: schedule.name } : null,
    }
    setPersistedScreen(persistedScreen)

    // Send screen info to native layer for settings menu
    setScreenInfo(screen)

    // Sync local playback setting to native layer (for OkHttp caching behavior)
    syncLocalPlayback(screen.localPlayback || false, screen.platform)

    // Set cacheable resources for progress tracking (only when local playback is enabled)
    // Video resources initially have just the manifest URL; actual segment URLs are
    // resolved later by VideoPlayer via updateCacheableVideoUrls.
    if (screen.localPlayback) {
      // A schedule reaches its media through events the screen payload does not carry, so the
      // server resolves that list; everything else is already inline.
      const resources = screen.schedule
        ? await fetchScheduleMedia(screen.schedule.id, screen.imageQuality)
        : getCachablePlayablesUrls(screen)
      setCacheTargets(resources)
      log('Set cacheable resources:', resources.length, 'items')
    }

    return screen
  } catch (e) {
    // If local playback is enabled and we have persistent screen, use it as fallback
    if (isNetworkError(e)) {
      const persistedScreen = getPersistedScreen()
      // if (persistedScreen?.localPlayback) {
      if (persistedScreen) {
        log('Network unavailable, using persisted screen data for local playback')
        return persistedScreen
      }
    }
    throw e
  }
}

/** Falls back to caching nothing rather than blocking start-up when the list cannot be read. */
async function fetchScheduleMedia(scheduleId: string, imageQuality?: Screen['imageQuality']) {
  try {
    const response = await fetch(resolveUrl(`schedule/${scheduleId}/media`))
    if (!response.ok) return []
    const media = await response.json()
    return media.map((content: Parameters<typeof toCacheableResource>[0]) => toCacheableResource(content, imageQuality))
  } catch {
    return []
  }
}

export const ScreenManager = () => {
  const [paircode, setPaircode] = useState('')
  const [screen, setScreen] = useState<Screen>()

  useEffect(() => {
    async function init() {
      const canPlayUnmutedMedia = await getAutoplayCheckPromise().catch((err) => {
        log('Autoplay capability check failed before screen registration', err)
        return false
      })

      const _screen = await getScreenInfo(canPlayUnmutedMedia)

      // Always try to register with server first, falls back to cache if offline
      const screen = await register(_screen)
      setScreen(screen)
      setPaircode(screen.paircode || '')
      initSSE(screen.id)
    }

    init()
  }, [])

  // Health ping interval - only when screen is paired
  useEffect(() => {
    if (!screen?.id || paircode) return

    const performHealthPing = async () => {
      try {
        const response = await sendHealthPing(screen.id)

        ensureSSE()

        // Sync local playback setting if changed
        if (response.localPlayback !== screen.localPlayback) {
          log('Local playback setting changed:', response.localPlayback)
          syncLocalPlayback(response.localPlayback, screen.platform)

          const persistedScreen = getPersistedScreen()
          if (persistedScreen) {
            setPersistedScreen({ ...persistedScreen, localPlayback: response.localPlayback })
          }
          // Update state to trigger re-render (for banner)
          setScreen({ ...screen, localPlayback: response.localPlayback })
        }
      } catch (error) {
        log('Health ping failed:', error)
      }
    }

    // Perform initial ping
    performHealthPing()

    // Set up interval
    const intervalId = setInterval(performHealthPing, SCREEN_HEALTH_PING_INTERVAL_MS)

    return () => {
      clearInterval(intervalId)
    }
  }, [screen?.id, screen?.localPlayback, paircode])

  if (!screen) return null

  return (
    <ScreenContainer screen={screen}>
      {paircode ? (
        <Pairing paircode={paircode} allowCopy={COPYABLE_PAIRCODE_PLATFORMS.has(screen.platform)} />
      ) : (
        <SourcePlayer screen={screen} />
      )}
    </ScreenContainer>
  )
}
